import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { mayTestMeasurement } from '@/lib/analytics'
import { useAuthStore } from '@/stores/auth'
import { forgetStaleChunk, recoverFromStaleChunk } from './staleChunk'

const APP_NAME = 'easyrechnung'

declare module 'vue-router' {
  interface RouteMeta {
    /** Appended before the app name in `document.title`. */
    title?: string
    /** Reachable without a session. */
    public?: boolean
  }
}

const routes: RouteRecordRaw[] = [
  {
    // The children carry absolute paths so this wrapper never competes with the
    // app shell for `/`.
    path: '/auth',
    component: () => import('@/layouts/AuthLayout.vue'),
    redirect: { name: 'login' },
    meta: { public: true },
    children: [
      {
        path: '/login',
        name: 'login',
        component: () => import('@/views/auth/LoginView.vue'),
        meta: { title: 'Anmelden', public: true },
      },
      {
        path: '/register',
        name: 'register',
        component: () => import('@/views/auth/RegisterView.vue'),
        meta: { title: 'Registrieren', public: true },
      },
      {
        path: '/invite/:token',
        name: 'invite',
        component: () => import('@/views/auth/InviteView.vue'),
        meta: { title: 'Einladung', public: true },
        props: true,
      },
      {
        path: '/forgot-password',
        name: 'forgot-password',
        component: () => import('@/views/auth/ForgotPasswordView.vue'),
        meta: { title: 'Passwort vergessen', public: true },
      },
      {
        // Target of the link in the password-reset mail (`?token=`).
        path: '/reset-password',
        name: 'reset-password',
        component: () => import('@/views/auth/ResetPasswordView.vue'),
        meta: { title: 'Neues Passwort', public: true },
      },
      {
        // Target of the link in the welcome mail (`?token=`).
        path: '/confirm-email',
        name: 'confirm-email',
        component: () => import('@/views/auth/ConfirmEmailView.vue'),
        meta: { title: 'E-Mail bestätigen', public: true },
      },
    ],
  },
  {
    path: '/',
    component: () => import('@/layouts/AppLayout.vue'),
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue'),
        meta: { title: 'Dashboard' },
      },

      {
        // Bewusst nicht in der Seitennavigation: der Einstieg ist die
        // Dashboard-Karte, danach ist die Route über sie erreichbar.
        path: 'onboarding',
        name: 'onboarding',
        component: () => import('@/views/onboarding/OnboardingView.vue'),
        meta: { title: 'Einrichtung' },
      },

      {
        path: 'rechnungen',
        name: 'invoices',
        component: () => import('@/views/invoices/InvoiceListView.vue'),
        meta: { title: 'Rechnungen' },
      },
      {
        path: 'rechnungen/neu',
        name: 'invoice-new',
        component: () => import('@/views/invoices/InvoiceCreateView.vue'),
        meta: { title: 'Neue Rechnung' },
      },
      {
        // Same reason as the literal paths below: ahead of `rechnungen/:id`.
        path: 'rechnungen/uebernehmen',
        name: 'invoice-import',
        component: () => import('@/views/invoices/InvoiceImportView.vue'),
        meta: { title: 'Rechnung übernehmen' },
      },
      {
        // Must stay ahead of `rechnungen/:id`, otherwise the id route wins.
        path: 'rechnungen/offene-posten',
        name: 'invoice-open-items',
        component: () => import('@/views/invoices/OpenItemsView.vue'),
        meta: { title: 'Offene Posten' },
      },
      {
        // Same reason — and `neu` has to precede the id below it.
        path: 'rechnungen/wiederkehrend',
        name: 'recurring',
        component: () => import('@/views/invoices/RecurringListView.vue'),
        meta: { title: 'Dauerrechnungen' },
      },
      {
        path: 'rechnungen/wiederkehrend/neu',
        name: 'recurring-new',
        component: () => import('@/views/invoices/RecurringEditView.vue'),
        meta: { title: 'Neue Dauerrechnung' },
      },
      {
        path: 'rechnungen/wiederkehrend/:id',
        name: 'recurring-edit',
        component: () => import('@/views/invoices/RecurringEditView.vue'),
        meta: { title: 'Dauerrechnung' },
        props: true,
      },
      {
        path: 'rechnungen/:id',
        name: 'invoice-detail',
        component: () => import('@/views/invoices/InvoiceDetailView.vue'),
        meta: { title: 'Rechnung' },
        props: true,
      },

      {
        path: 'ausgaben',
        name: 'expenses',
        component: () => import('@/views/expenses/ExpenseListView.vue'),
        meta: { title: 'Ausgaben' },
      },
      {
        path: 'ausgaben/neu',
        name: 'expense-new',
        component: () => import('@/views/expenses/ExpenseCreateView.vue'),
        meta: { title: 'Neue Ausgabe' },
      },
      {
        path: 'ausgaben/:id',
        name: 'expense-detail',
        component: () => import('@/views/expenses/ExpenseDetailView.vue'),
        meta: { title: 'Ausgabe' },
        props: true,
      },

      {
        path: 'belege',
        name: 'documents',
        component: () => import('@/views/documents/DocumentInboxView.vue'),
        meta: { title: 'Belegeingang' },
      },

      {
        path: 'kunden',
        name: 'customers',
        component: () => import('@/views/contacts/CustomerListView.vue'),
        meta: { title: 'Kunden' },
      },
      {
        path: 'kunden/:id',
        name: 'customer-detail',
        component: () => import('@/views/contacts/CustomerDetailView.vue'),
        meta: { title: 'Kunde' },
        props: true,
      },
      {
        path: 'lieferanten',
        name: 'suppliers',
        component: () => import('@/views/contacts/SupplierListView.vue'),
        meta: { title: 'Lieferanten' },
      },
      {
        // Customers and suppliers share one file; the view adapts to the kind.
        path: 'lieferanten/:id',
        name: 'supplier-detail',
        component: () => import('@/views/contacts/CustomerDetailView.vue'),
        meta: { title: 'Lieferant' },
        props: true,
      },

      {
        path: 'anlagen',
        name: 'assets',
        component: () => import('@/views/assets/AssetListView.vue'),
        meta: { title: 'Anlagen' },
      },
      {
        path: 'anlagen/:id',
        name: 'asset-detail',
        component: () => import('@/views/assets/AssetDetailView.vue'),
        meta: { title: 'Anlage' },
        props: true,
      },

      {
        path: 'bank',
        name: 'bank',
        component: () => import('@/views/banking/BankTransactionsView.vue'),
        meta: { title: 'Bankumsätze' },
      },
      {
        path: 'bank/konten',
        name: 'bank-accounts',
        component: () => import('@/views/banking/BankAccountsView.vue'),
        meta: { title: 'Bankkonten' },
      },

      {
        path: 'buchungen',
        name: 'journal',
        component: () => import('@/views/ledger/JournalView.vue'),
        meta: { title: 'Buchungen' },
      },

      {
        path: 'auswertungen',
        name: 'reports',
        component: () => import('@/views/reports/ReportHubView.vue'),
        meta: { title: 'Auswertungen' },
      },
      {
        path: 'auswertungen/uva',
        name: 'report-uva',
        component: () => import('@/views/reports/UvaView.vue'),
        meta: { title: 'Umsatzsteuervoranmeldung' },
      },
      {
        path: 'auswertungen/ear',
        name: 'report-ear',
        component: () => import('@/views/reports/EarView.vue'),
        meta: { title: 'Einnahmen-Ausgaben-Rechnung' },
      },
      {
        path: 'auswertungen/e1a',
        name: 'report-e1a',
        component: () => import('@/views/reports/E1aView.vue'),
        meta: { title: 'E1a-Beilage' },
      },
      {
        path: 'auswertungen/u1',
        name: 'report-u1',
        component: () => import('@/views/reports/U1View.vue'),
        meta: { title: 'Umsatzsteuererklärung' },
      },
      {
        path: 'auswertungen/zm',
        name: 'report-zm',
        component: () => import('@/views/reports/ZmView.vue'),
        meta: { title: 'Zusammenfassende Meldung' },
      },
      {
        path: 'auswertungen/anlagenverzeichnis',
        name: 'report-assets',
        component: () => import('@/views/reports/AssetRegisterView.vue'),
        meta: { title: 'Anlagenverzeichnis' },
      },

      // One view for the overview and the article; which face it shows is
      // decided by the `slug` param alone.
      {
        path: 'hilfe',
        name: 'help',
        component: () => import('@/views/HelpView.vue'),
        meta: { title: 'Hilfe' },
      },
      {
        path: 'hilfe/:slug',
        name: 'help-article',
        component: () => import('@/views/HelpView.vue'),
        meta: { title: 'Hilfe' },
      },

      {
        path: 'neuerungen',
        name: 'changelog',
        component: () => import('@/views/ChangelogView.vue'),
        meta: { title: 'Neuerungen' },
      },

      {
        path: 'rechtsgrundlagen',
        name: 'legal-basis',
        component: () => import('@/views/LegalBasisView.vue'),
        meta: { title: 'Rechtsgrundlagen' },
      },

      // Die drei Vertragsdokumente. Eine Ansicht, drei Routen — `documentKey`
      // entscheidet, welches Dokument die API liefert.
      {
        path: 'nutzungsbedingungen',
        name: 'terms',
        component: () => import('@/views/legal/LegalDocumentView.vue'),
        meta: { title: 'Nutzungsbedingungen' },
        props: { documentKey: 'agb' },
      },
      {
        path: 'datenschutzerklaerung',
        name: 'privacy-policy',
        component: () => import('@/views/legal/LegalDocumentView.vue'),
        meta: { title: 'Datenschutzerklärung' },
        props: { documentKey: 'datenschutz' },
      },
      {
        path: 'auftragsverarbeitung',
        name: 'dpa',
        component: () => import('@/views/legal/LegalDocumentView.vue'),
        meta: { title: 'Auftragsverarbeitungsvertrag' },
        props: { documentKey: 'avv' },
      },

      {
        path: 'einstellungen',
        component: () => import('@/views/settings/SettingsView.vue'),
        meta: { title: 'Einstellungen' },
        children: [
          { path: '', redirect: '/einstellungen/unternehmen' },
          {
            path: 'unternehmen',
            name: 'settings-company',
            component: () => import('@/views/settings/CompanyView.vue'),
            meta: { title: 'Unternehmen' },
          },
          {
            path: 'steuerjahre',
            name: 'settings-fiscal-years',
            component: () => import('@/views/settings/FiscalYearsView.vue'),
            meta: { title: 'Steuerjahre' },
          },
          {
            path: 'kategorien',
            name: 'settings-categories',
            component: () => import('@/views/settings/CategoriesView.vue'),
            meta: { title: 'Kategorien' },
          },
          {
            path: 'nummernkreise',
            name: 'settings-number-ranges',
            component: () => import('@/views/settings/NumberRangesView.vue'),
            meta: { title: 'Nummernkreise' },
          },
          {
            path: 'rechnungskreise',
            name: 'settings-invoice-circles',
            component: () => import('@/views/settings/NumberCirclesView.vue'),
            meta: { title: 'Rechnungskreise' },
          },
          {
            path: 'regeln',
            name: 'settings-rules',
            component: () => import('@/views/settings/RulesView.vue'),
            meta: { title: 'Regeln' },
          },
          {
            path: 'belegerkennung',
            name: 'settings-ai',
            component: () => import('@/views/settings/AiView.vue'),
            meta: { title: 'Belegerkennung' },
          },
          {
            path: 'benachrichtigungen',
            name: 'settings-notifications',
            component: () => import('@/views/settings/NotificationsView.vue'),
            meta: { title: 'Benachrichtigungen' },
          },
          {
            path: 'benutzer',
            name: 'settings-users',
            component: () => import('@/views/settings/UsersView.vue'),
            meta: { title: 'Benutzer' },
          },
          {
            path: 'api-schluessel',
            name: 'settings-api-keys',
            component: () => import('@/views/settings/ApiKeysView.vue'),
            meta: { title: 'API-Schlüssel' },
          },
          {
            path: 'abo',
            name: 'settings-subscription',
            component: () => import('@/views/settings/SubscriptionView.vue'),
            meta: { title: 'Abo' },
          },
          {
            path: 'konto',
            name: 'settings-account',
            component: () => import('@/views/settings/AccountView.vue'),
            meta: { title: 'Mein Konto' },
          },
          {
            path: 'datenschutz',
            name: 'settings-privacy',
            component: () => import('@/views/settings/PrivacyView.vue'),
            meta: { title: 'Daten und Datenschutz' },
          },
          {
            path: 'ga4-test',
            name: 'settings-ga4-test',
            component: () => import('@/views/settings/Ga4TestView.vue'),
            meta: { title: 'GA4-Test' },
            /*
             * Der Reiter ist für alle anderen unsichtbar, die Adresse ist es
             * nicht. Without this line anyone who guesses `/einstellungen/ga4-test`
             * — or gets the link from someone — could fire events into the
             * property the advertising numbers come from. The global guard has
             * already established the session by the time this runs.
             */
            beforeEnter: () =>
              mayTestMeasurement(useAuthStore().user?.email) ? true : { path: '/' },
          },
        ],
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: 'Nicht gefunden', public: true },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, saved) {
    if (saved) return saved
    if (to.hash) return { el: to.hash, behavior: 'smooth' }
    return { top: 0 }
  },
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  // One silent refresh attempt per page load decides whether a session exists.
  if (!auth.bootstrapped) await auth.bootstrap()

  if (to.meta.public) {
    if (auth.isAuthenticated && (to.name === 'login' || to.name === 'register')) {
      return { path: '/' }
    }
    return true
  }

  if (!auth.isAuthenticated) {
    return { name: 'login', query: to.fullPath === '/' ? {} : { redirect: to.fullPath } }
  }
  return true
})

router.afterEach((to) => {
  const title = to.meta.title
  document.title = title ? `${title} · ${APP_NAME}` : APP_NAME
  forgetStaleChunk({ storage: sessionStorage })
})

/*
 * A window opened before a deploy asks for chunks that are gone, and the
 * screen would simply stay away. Loading the address once more fetches the
 * shell of the build that is actually deployed.
 */
router.onError((error, to) => {
  recoverFromStaleChunk(error, to.fullPath, {
    storage: sessionStorage,
    reload: (path) => window.location.assign(path),
  })
})

export default router
